import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import ReorderItems from '../reorderItems';
import AttributeChip from '../attributeChip';
import AttributeChipWithScore from '../attributeChipWithScore';
import Scrollbars from '../scrollbars';
import { reorder, isItemInList } from '../../../utility/helpers';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    gap: 4,
    width: '100%',
  },
  horizontalList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  attribute: {
    minWidth: 0,
  },
  draggableItem: {
    display: 'flex',
    minWidth: 0,
  },
  scrollViewport: {
    maxHeight: 124,
  },
  scrollY: {
    right: -8,
  },
}));

const AttributesList = ({
  className = undefined,
  scrollYclass = null,
  attributeClass = undefined,
  translations = {},
  attributes,
  selectedAttributes = [],
  isHorizontalList = false,
  isActive = false,
  hasAnswerComment = false,
  checkIsSelectable = () => true,
  hasScore = false,
  isDraggable = false,
  isRemovable = false,
  isScrollable = false,
  onSelect = () => {},
  onReorder = () => {},
  onRemove = () => {},
}) => {
  const classes = useStyles();

  const handleReorderAttributes = useCallback(
    result => {
      if (
        !result.destination ||
        result.source.index === result.destination.index
      )
        return;

      const updatedOrder = reorder(
        attributes,
        result.source.index,
        result.destination.index
      );

      return onReorder(updatedOrder);
    },
    [attributes, onReorder]
  );

  return (
    <Scrollbars
      verticalScrollClass={classNames(classes.scrollY, scrollYclass)}
      viewportClass={classNames({ [classes.scrollViewport]: isScrollable })}
    >
      <ReorderItems
        className={classNames(
          classes.root,
          {
            [classes.horizontalList]: isHorizontalList,
          },
          className
        )}
        itemClass={classes.draggableItem}
        isDragDisabled={!isDraggable || isHorizontalList}
        onSave={handleReorderAttributes}
      >
        {attributes.map(attribute => {
          const isSelectable = checkIsSelectable(attribute);
          const isSelected =
            isSelectable && isItemInList(selectedAttributes, attribute);

          return hasScore ? (
            <AttributeChipWithScore
              key={`value${attribute.id}`}
              id={attribute.id}
              translations={translations}
              attribute={attribute}
              hasRemove={isRemovable}
              isDraggable={isDraggable}
              onRemove={onRemove}
            />
          ) : (
            <AttributeChip
              key={`value${attribute.id}`}
              id={attribute.id}
              customClass={classNames(classes.attribute, attributeClass)}
              attribute={attribute}
              name={attribute.name}
              color={isSelected || isActive ? attribute?.color : ''}
              hasRemove={isRemovable}
              hasAnswerComment={
                hasAnswerComment && attribute.has_answer_comment
              }
              isAttributeDraggable={isDraggable}
              isSelectable={isSelectable && !isActive}
              isSelected={isActive || isSelected}
              onSelect={onSelect}
              onRemove={onRemove}
            />
          );
        })}
      </ReorderItems>
    </Scrollbars>
  );
};

AttributesList.propTypes = {
  className: PropTypes.string,
  scrollYclass: PropTypes.string,
  attributeClass: PropTypes.string,
  translations: PropTypes.object,
  attributes: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedAttributes: PropTypes.arrayOf(PropTypes.object),
  isHorizontalList: PropTypes.bool,
  hasScore: PropTypes.bool,
  isDraggable: PropTypes.bool,
  isRemovable: PropTypes.bool,
  isActive: PropTypes.bool,
  hasAnswerComment: PropTypes.bool,
  isScrollable: PropTypes.bool,
  checkIsSelectable: PropTypes.func,
  onSelect: PropTypes.func,
  onReorder: PropTypes.func,
  onRemove: PropTypes.func,
};

export default AttributesList;
